import { AddToCart } from "@components/addtocart";
import { Star } from "@components/star";
import { TProduct } from "@components/types";
import { getFirst, getImageObject, getSafeUrl, humanizeCurrency, sanityIoImageLoader } from '@core/utils';
import { Store } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { FaStar } from "react-icons/fa";

type Props = {
    product: TProduct;
    showStoreName?: boolean
};


export const ProductCardWithProvider: React.FC<Props> = ({ product, showStoreName = false }) => {
    let firstInventory = getFirst(product?.inventories)
    let storeRating = firstInventory?.store?.stats?.rating_overall;
    let isFnB = firstInventory?.store?.category_id == 1;
    let externalLink = firstInventory?.external_link || "";
    let isOnline = externalLink?.length > 0;
    let isAvailable = product?.inventories?.length > 0 && getFirst(product?.inventories)?.is_available !== false;
    // No store attached means there is no shop page to open: go to the product page.
    let hasStore = firstInventory?.store_id > 0 || firstInventory?.store?.id > 0;
    let productHref = showStoreName && hasStore && (product?.inventories?.[0]?.store?.category_id === 1 || product?.inventories?.[0]?.store?.category_id === undefined || product?.inventories?.[0]?.store?.category_id === null) ? `/shop/${firstInventory?.store_id}/${getSafeUrl(product?.inventories?.[0]?.store?.name)}?product_id=${product?.id}` : `/product/${product?.id}/${getSafeUrl(product?.name)}?store_id=${firstInventory?.store_id}`;

    return (
        <div className="box-border relative min-h-36 max-w-120 flex justify-center flex-col bg-white border border-neutral-200/70 shadow-sm hover:shadow-md rounded-2xl font-manrope overflow-hidden transition-shadow">
            {isOnline &&
                <div className="flex justify-between items-center gap-2 px-3 py-2 border-b border-neutral-100 bg-neutral-50">
                    <span className="font-semibold text-sm text-neutral-800 line-clamp-1">{firstInventory?.store?.name}
                    </span>
                    {externalLink?.length > 0 &&
                        <div className="flex gap-1 items-center flex-shrink-0">
                            <Link href={externalLink} >
                                <Image
                                    loader={sanityIoImageLoader}
                                    src={firstInventory?.store?.logo || "https://cdn.jhattse.com/public/assets/noimage.png"}
                                    width="28"
                                    height="28"
                                    className="rounded-md w-7 h-7 object-cover"
                                    alt={firstInventory?.store?.name || ''}
                                />
                            </Link>
                        </div>
                    }
                </div>
            }
            {isFnB &&
                <div className="flex justify-between items-center gap-2 px-3 py-2 border-b border-neutral-100 bg-neutral-50">
                    <span className="font-semibold text-sm text-neutral-800 line-clamp-1">{firstInventory?.store?.name}
                    </span>
                    {storeRating !== undefined && storeRating > 0 &&
                        <div className="flex gap-1 items-center flex-shrink-0 text-xs font-bold text-neutral-600">
                            {storeRating?.toFixed(1)}
                            <FaStar className="text-store_yellow" size={11} />
                        </div>
                    }
                </div>
            }
            {product?.tag &&
                <div className="absolute top-2 left-2 z-10 w-max rounded-full font-semibold text-white text-xs bg-brand-500 shadow-sm py-1 px-2.5 select-none">
                    {product.tag}
                </div>
            }
            <div className="flex flex-row md:gap-3 gap-2.5 items-stretch p-2.5">
                <div className="flex justify-start flex-shrink-0">
                    <Link href={productHref} prefetch={false}>
                        <div className="relative md:h-32 md:w-32 h-28 w-28 rounded-xl overflow-hidden bg-neutral-100">
                            <Image
                                loader={sanityIoImageLoader}
                                src={getImageObject(product?.images, product?.category?.image)?.url}
                                alt={getImageObject(product?.images)?.description || product?.name || 'Product'}
                                fill
                                sizes="128px"
                                className="object-cover scale-100 hover:scale-105 focus:scale-105 ease-in duration-200"
                            />
                        </div>
                    </Link>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-col justify-between h-full font-manrope overflow-hidden">
                        <div className="flex flex-col gap-1">
                            <Link href={productHref} prefetch={false}>
                                <p className="font-semibold line-clamp-2 md:text-base text-sm text-neutral-900">{product?.name}</p>
                            </Link>
                            {showStoreName && <div className="text-xs text-neutral-500 flex items-center gap-1 font-medium">
                                <Store size={11} className="flex-shrink-0" />
                                <span className="line-clamp-1">{product?.inventories?.[0]?.store?.name}</span>
                            </div>}
                            {
                                product?.stats != null && product?.stats != undefined && product?.stats?.rating_count > 0 ?
                                    <Star rating={product?.stats?.rating_overall} />
                                    :
                                    <div className="text-xs text-neutral-400 font-manrope font-medium">No ratings yet</div>
                            }
                        </div>
                        <div className="flex flex-row gap-2 justify-between items-center pt-2">
                            <div className="text-business-500 font-bold md:text-lg text-base flex-shrink-0">
                                <span>{humanizeCurrency(getFirst(product?.inventories)?.price || product?.mrp)}</span>
                            </div>
                            {product?.inventories?.length != 0 ?
                                <div className="flex flex-row items-center max-w-32">
                                    <AddToCart addToCartBag={false} product={product} inventory={getFirst(product?.inventories)} isAvailable={isAvailable} btnStyle="text-business-500"></AddToCart>
                                </div>
                                :
                                <div className="text-xs font-semibold text-error-500 bg-error-100 px-2 py-1 rounded-full">Unavailable</div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}